import * as React from 'react'
import ReactDOMServer from 'react-dom/server'
import {Lang} from 'yougile-lang'
import {logger, Time, UUID} from 'yougile-util'
import {DataObjectId, isDataObjectId, User, UserAccount} from 'yougile-model'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {cn} from '@containers/html-utils'
import {ClickOut} from '../../common/utils-ui'
import {Accounts} from '../client/accounts'
import {PaymentClient} from '../client/payment-client'
import {Sync} from '../client/sync'
import {UserAvatarUtils} from '../extensions/user-avatar-utils'
import {UserInfoCard} from './user-info-card'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  user?: User
  userId?: DataObjectId
  guestId?: string
  opacity?: number
  showStatus?: boolean
  withUserInfo?: boolean
  size?: number
  guestName?: string
  guestAvatar?: string
}

interface State {
  infoOpened: boolean
}

export class UserAvatar extends React.Component<Props, State> {
  private updateHandlerId: UUID
  private element: HTMLElement | null
  private clickOutId: number

  public static renderToElement(props: Props, element: HTMLElement): void {
    element.innerHTML = ReactDOMServer.renderToString(<UserAvatar {...props} />)
  }

  constructor(props: Props) {
    super(props)
    this.state = {
      infoOpened: false
    }
  }

  public override componentDidMount(): void {
    const userId = this.props.userId || this.props.user?.id
    if (!userId) {
      return
    }
    const update = () => {
      if (!userId) {
        logger.warn('No user at element', this.element)
        return
      }
      const acc = Accounts.get(userId, true)
      this.forceUpdate()
      if (UserAccount.isInDnd(acc)) {
        setTimeout(() => update(), acc.dndUntil - Time.now() + 1000)
      }
    }
    update()
    this.updateHandlerId = Accounts.addListener({account: userId, cb: update})
  }

  public override componentWillUnmount(): void {
    if (this.updateHandlerId) {
      Accounts.removeListener(this.updateHandlerId)
    }
    if (this.clickOutId) {
      ClickOut.offClickOut(this.clickOutId)
    }
  }

  public onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const {user, userId, withUserInfo, onClick} = this.props
    if (withUserInfo) {
      const u =
        user || (userId && (Sync.getCurrentSandbox().get(userId) as User))
      if (u && this.element) {
        const rect = this.element.getBoundingClientRect()
        UserInfoCard.show(u, rect.left + rect.width / 2, rect.top + rect.height)
      }
    }
    onClick?.(e)
  }

  public override render(): React.ReactNode {
    const {
      user,
      userId = user?.id,
      opacity = 1,
      showStatus: withStatus = false,
      size,
      title,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      withUserInfo: _,
      guestId,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      guestName: _guestName,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      guestAvatar: _guestAvatar,
      ...domProps
    } = this.props

    if (!userId) {
      return null
    }
    const box = Sync.getCurrentSandbox()
    const findEmail = (uid: DataObjectId) => {
      if (box?.get(uid)) {
        return (box.get(uid) as User).email || ''
      } else {
        return ''
      }
    }
    const email = user ? user.email : findEmail(userId)
    const account = Accounts.get(userId)
    const isDisallowed = PaymentClient.isUserDisallowed(box.companyId, userId)
    const inDnd = UserAccount.isInDnd(account)

    const guestInfo = guestId && SharedBoardsClient.getGuestInfo(guestId)
    const isRegisteredGuest = isDataObjectId(guestId)

    const realName = guestInfo ? guestInfo.name : Accounts.getName(userId)
    let statusClass =
      'user-avatar-status ' +
      (account.status === 'online'
        ? 'user-avatar-status-online'
        : 'user-avatar-status-offline')
    if (inDnd) {
      statusClass += ' user-avatar-status-dnd'
    }
    let statusHint = Lang.msg.userOnline
    if (account.status === 'offline') {
      statusHint = account.lastActivity
        ? Lang.msg.userOfflineSeenAt + Time.formatDateTime(account.lastActivity)
        : Lang.msg.userOffline
    } else if (inDnd) {
      statusHint = Lang.msg.userDnd + Time.formatDateTime(account.dndUntil)
    }
    statusHint = Lang.msg.user + ': ' + realName + '. ' + statusHint
    const style = {
      ...(size
        ? {
            width: size + 'px',
            height: size + 'px',
            lineHeight: size + 'px',
            fontSize: Math.ceil(size / 2.2) + 'px'
          }
        : {}),
      background: UserAvatarUtils.avaColorByName(realName, email, opacity)
    }
    const bgColor =
      guestId && !isRegisteredGuest
        ? UserAvatarUtils.avaColorByName(guestId, '')
        : undefined

    const avatar =
      isRegisteredGuest && guestInfo ? guestInfo?.avatar : account.avatar
    const getPhotoBg = () => 'url("' + UserAvatarUtils.preview(avatar) + '")'

    const photo = avatar ? (
      <div
        className="user-avatar-photo"
        style={{backgroundImage: getPhotoBg(), backgroundColor: bgColor}}
      />
    ) : undefined
    const status = withStatus ? (
      <div className={statusClass}>
        <div className="user-avatar-status-inner" />
      </div>
    ) : undefined
    const className = cn(
      'user-avatar',
      this.props.className,
      email && account.realName === '_' && 'invited',
      isDisallowed && 'disallowed'
    )

    return (
      <div
        {...domProps}
        className={className}
        style={{...style, ...(this.props.style ?? {})}}
        title={typeof title === 'string' ? title : statusHint}
        onClick={this.onClick}
        ref={(el: HTMLElement | null) => (this.element = el)}
      >
        {photo}
        {status}
        {UserAvatarUtils.avaShortName(realName, {id: userId, email})}
      </div>
    )
  }
}
